import { useIntl } from 'react-intl'

import useVerifyAuth from 'app/features/ListingFeature/hooks/useVerifyAuth'
import Alert from 'app/services/Alert'
import ErrorHandler, { isErrorHandlerType } from 'app/services/ErrorHandler'
import {
  useCreateLikedListingMutation,
  useDeleteLikedListingMutation
} from 'app/services/Query/listing/listing.mutation'
import useAuthStore, { getIsConfirmed } from 'app/store/auth.store'
import { IS_NATIVE_APP } from 'app/utils/constants/env.constants'
import { hasStatusError } from 'app/utils/helpers/type.helpers'

import useToggleRequest from './useToggleRequest'

interface UseLikeListingProps {
  listingId: string
  isLiked: boolean
}

const useLikeListing = ({ listingId, isLiked }: UseLikeListingProps) => {
  const { formatMessage } = useIntl()
  const checkAuth = useVerifyAuth()
  const isAuth = useAuthStore(getIsConfirmed)
  const { mutateAsync: createLike } = useCreateLikedListingMutation()
  const { mutateAsync: deleteLike } = useDeleteLikedListingMutation()

  const {
    state: liked,
    toggle,
    isLoading
  } = useToggleRequest({
    state: isLiked,
    onToggleRequest: () =>
      isLiked ? deleteLike(listingId) : createLike(listingId),
    onSuccess: () => {
      if (!IS_NATIVE_APP) {
        Alert.success(
          isLiked
            ? formatMessage({ defaultMessage: 'Your Like was deleted' })
            : formatMessage({ defaultMessage: 'Your Like was added' })
        )
      }
    },
    onError: (error) => {
      const errorCodes = [401, 403, 419]

      if (hasStatusError(error)) {
        if (errorCodes.includes(error?.status)) {
          checkAuth()
        } else {
          if (isErrorHandlerType(error)) new ErrorHandler(error?.data)
        }
      }
    }
  })

  return { liked, isLoading, toggleLike: isAuth ? toggle : () => checkAuth() }
}

export default useLikeListing
